import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmAction from '../../components/Buttons/ConfirmAction';
import ButtonComponent from '../../components/UI/Button/Button.component';
import { apiRoutes } from '../../constants/api-routes.constants';
import { Interests as InterestsEnum } from '../../enums/interests.enum';
import { UPDATE_FEED_PREFERENCES } from '../../graphql/resolvers/feed/feedMutations/updateFeedPreferences.gql';
import { UPDATE_USER_MUTATION } from '../../graphql/resolvers/userMutations/updateUser.gql';
import { humanize } from '../../helpers/interestsTaxonomy';
import { isSafeRedirectPath } from '../../helpers/isSafeRedirectPath';
import { userUpdateInitialState } from '../../initial-states/userUpdate.initialState';
import type { UserSliceState } from '../../interfaces/user.interface';
import { userSlice } from '../../store/user.store';
import { SimpleDialog } from '../UI/SimpleDialog';
import './interests.scss';

interface UserState {
  userStore: UserSliceState;
}

const Interests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: UserState) => state.userStore);
  // TRIL-322: this page doubles as the post-onboarding edit surface. A
  // returning user (!newUser) reaching it from the avatar menu edits their
  // existing interests; a brand-new user (newUser) is going through onboarding.
  const isEditMode = !user.newUser;
  const [interestsAdded, setInterestsAdded] = React.useState<string[]>(
    isEditMode ? (user.interests ?? []) : [],
  );

  const { UPDATE_USER } = userSlice.actions;

  // TRIL-478: onboarding-only mode choice shown before the interests pills.
  const [step, setStep] = React.useState<'mode' | 'interests'>(
    isEditMode ? 'interests' : 'mode',
  );
  const [feedMode, setFeedMode] = React.useState<'CURATED' | 'TRENDING'>(
    'CURATED',
  );
  const [updateFeedPreferences, { error: feedModeError }] = useMutation(
    UPDATE_FEED_PREFERENCES,
  );

  const [updateUser, { error: updateError }] = useMutation(
    UPDATE_USER_MUTATION,
    {
      variables: {
        input: {
          ...userUpdateInitialState,
          interests: interestsAdded,
          newUser: false,
          id: user.id,
        },
      },
      onCompleted: ({ updateUser }) => {
        if (updateUser) {
          const updatedUserObject = {
            ...user,
            interests: interestsAdded,
            newUser: false,
          };
          dispatch(UPDATE_USER(updatedUserObject));
          // TRIL-322: in edit mode return the user to where they came from.
          // TRIL-459: first-login onboarding honors a validated `from` (the
          // anon-reader funnel's post) when present, else lands on the feed.
          if (isEditMode) {
            navigate(-1);
          } else {
            const fromState = (location.state as { from?: unknown } | null)
              ?.from;
            const from = isSafeRedirectPath(fromState) ? fromState : null;
            navigate(
              from ?? `${apiRoutes.USER}/${user.username}${apiRoutes.FEED}`,
            );
          }
        }
      },
      // Errors surface via Apollo's mutation state; defining onError keeps the
      // mutate promise from rejecting (nothing user-facing to do here).
      onError: () => undefined,
    },
  );

  // TRIL-323: track the originally-loaded selection so Save is only enabled
  // once the user has actually added or removed a category.
  const originalInterestsRef = React.useRef<string[]>(
    isEditMode ? (user.interests ?? []) : [],
  );
  const isDirty =
    interestsAdded.length !== originalInterestsRef.current.length ||
    interestsAdded.some((i) => !originalInterestsRef.current.includes(i));

  // TRIL-323: Save opens a confirmation modal; its action button fires the
  // mutation.
  const [showConfirm, setShowConfirm] = React.useState(false);
  const handleConfirmSave = async () => {
    setShowConfirm(false);
    // TRIL-478: persist the chosen feed mode before flipping the newUser gate.
    // A rejected write returns null, and the early return keeps updateUser from
    // running — the gate stays newUser:true and the error surfaces below.
    if (!isEditMode) {
      const feedModeResult = await updateFeedPreferences({
        variables: { userId: user.id, preferences: { feedMode } },
      }).catch(() => null);
      if (!feedModeResult) return;
    }
    updateUser({
      variables: {
        input: { interests: interestsAdded, newUser: false, id: user.id },
      },
    });
  };

  // TRIL-323: toggle a category by value (the whole pill is the control).
  const toggleInterest = (interest: string) => {
    setInterestsAdded((prev) =>
      prev.includes(interest)
        ? prev.filter((item) => item !== interest)
        : [...prev, interest],
    );
  };

  if (step === 'mode') {
    return (
      <div>
        <h3 className="INTERESTS__TITLE">How should we build your feed?</h3>
        <p>You can pick topics next either way.</p>
        <ButtonComponent
          type="button"
          variant="primary"
          onClick={() => {
            setFeedMode('TRENDING');
            setStep('interests');
          }}
        >
          Trending — show me what's popular
        </ButtonComponent>
        <ButtonComponent
          type="button"
          variant="outline"
          onClick={() => {
            setFeedMode('CURATED');
            setStep('interests');
          }}
        >
          Curated — I'll pick my topics
        </ButtonComponent>
      </div>
    );
  }

  return (
    <div>
      <h3 className="INTERESTS__TITLE">User's Interests</h3>
      <div className="Grid">
        <div>
          <div className="INTERESTS__PILLS">
            {Object.values(InterestsEnum).map((interest: string) => {
              const selected = interestsAdded.includes(interest);
              return (
                <button
                  type="button"
                  key={interest}
                  className={`INTERESTS__PILL${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  aria-label={humanize(interest)}
                  onClick={() => toggleInterest(interest)}
                >
                  {humanize(interest)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="INTERESTS__ADDED">
          <h3>Selected Interests:</h3>
          {interestsAdded.length > 0 ? (
            <ul>
              {interestsAdded.map((interest: string) => (
                <li key={interest}>{humanize(interest)}</li>
              ))}
            </ul>
          ) : (
            <p>None selected</p>
          )}
          <ConfirmAction
            type={'button'}
            disabled={!isDirty}
            confirmActionLabel={isEditMode ? 'Save changes' : 'Add Interest(s)'}
            onClick={() => setShowConfirm(true)}
          />
          {feedModeError || updateError ? (
            <p className="INTERESTS__ERROR">Couldn't save. Try again.</p>
          ) : null}
          {/* TRIL-322: edit mode hides the nav rail (TRIL-68 full-bleed), so
              provide an explicit way back. */}
          {isEditMode && (
            <ButtonComponent
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Cancel
            </ButtonComponent>
          )}
        </div>
      </div>
      <SimpleDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        dialogTitleChildren={<>Save interests?</>}
        dialogBody="Save your selected interest categories?"
        closeModalLabel="Cancel"
        actionLabel="Yes, save"
        onSubmit={handleConfirmSave}
      />
    </div>
  );
};

export default Interests;
